import { useState, useCallback, useRef, useEffect } from "react";
import { surveySteps } from "../data/surveyQuestions";
import { CheckCircle } from "lucide-react";
import confetti from "canvas-confetti";

const SURVEY_API = process.env.REACT_APP_SURVEY_API_URL || "https://eamico-survey-api.eamico-inc.workers.dev";

/* ── Scratch Gift Card Screen ── */
const GiftCardScreen = ({ discountCode, sessionId }) => {
  const canvasRef = useRef(null);
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const scratchPercent = useRef(0);
  const isDrawing = useRef(false);
  const confettiFired = useRef(false);

  // Fire confetti on mount
  useEffect(() => {
    if (confettiFired.current) return;
    confettiFired.current = true;
    confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 }, colors: ["#ff9a4d", "#f5c842", "#ffb347", "#1a73e8", "#34a853"] });
  }, []);

  // Fire again when code is revealed
  useEffect(() => {
    if (!revealed) return;
    confetti({ particleCount: 120, spread: 90, origin: { y: 0.5 }, colors: ["#ff9a4d", "#f5c842", "#ffb347", "#34a853", "#ffffff"] });
  }, [revealed]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || revealed) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    // Draw scratch surface
    const grad = ctx.createLinearGradient(0, 0, rect.width, 0);
    grad.addColorStop(0, "#c0c0c0");
    grad.addColorStop(0.3, "#d4d4d4");
    grad.addColorStop(0.5, "#b8b8b8");
    grad.addColorStop(0.7, "#d4d4d4");
    grad.addColorStop(1, "#c0c0c0");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, rect.width, rect.height);

    // Scratch text
    ctx.fillStyle = "#999";
    ctx.font = "bold 14px Google Sans, Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("SCRATCH TO REVEAL", rect.width / 2, rect.height / 2);

    // Dots pattern
    ctx.fillStyle = "rgba(0,0,0,0.06)";
    for (let x = 0; x < rect.width; x += 8) {
      for (let y = 0; y < rect.height; y += 8) {
        ctx.beginPath();
        ctx.arc(x, y, 1, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }, [revealed]);

  const scratch = (x, y) => {
    const canvas = canvasRef.current;
    if (!canvas || revealed) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const cx = x - rect.left;
    const cy = y - rect.top;
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(cx, cy, 22, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = "source-over";

    // Check scratch percentage
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let clear = 0;
    for (let i = 3; i < imageData.data.length; i += 4) {
      if (imageData.data[i] === 0) clear++;
    }
    scratchPercent.current = clear / (imageData.data.length / 4);
    if (scratchPercent.current > 0.45) setRevealed(true);
  };

  const handleMove = (e) => {
    if (!isDrawing.current) return;
    const touch = e.touches ? e.touches[0] : e;
    scratch(touch.clientX, touch.clientY);
  };

  const copy = () => {
    navigator.clipboard.writeText(discountCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <CheckCircle className="w-12 h-12 text-[#34a853] mx-auto mb-3" />
          <h2 className="text-[22px] font-normal text-[#202124] mb-1">You're in.</h2>
          <p className="text-[14px] text-[#5f6368]">Thanks for the honest feedback. Scratch to reveal your discount code.</p>
        </div>

        {/* Gift Card */}
        <div className="rounded-[20px] overflow-hidden shadow-xl mb-6" data-testid="gift-card">
          {/* Card body */}
          <div className="bg-[#1a1a1a] px-8 pt-7 pb-5">
            <div className="flex items-center justify-between mb-7">
              <img src="https://customer-assets.emergentagent.com/job_eamico-core/artifacts/uowh0b6l_Untitled%20design.png" alt="Eamico" className="h-6 brightness-[10]" />
              <span className="text-[10px] text-[#666] uppercase tracking-[3px]">Discount Code</span>
            </div>
            <p className="text-[44px] font-bold text-white tracking-tight leading-none">$100<span className="text-[20px] font-normal text-[#888]"> off</span></p>

            {/* Scratch area */}
            <div className="relative mt-5 mb-1 rounded-lg overflow-hidden h-[52px] bg-[#2a2a2a] select-none">
              {/* Code underneath */}
              <div className="absolute inset-0 flex items-center justify-center">
                <p className="text-[22px] font-mono font-bold text-white tracking-[5px]" data-testid="discount-code">{discountCode}</p>
              </div>
              {/* Scratch canvas on top */}
              {!revealed && (
                <canvas
                  ref={canvasRef}
                  className="absolute inset-0 w-full h-full cursor-grab active:cursor-grabbing touch-none"
                  onMouseDown={() => { isDrawing.current = true; }}
                  onMouseUp={() => { isDrawing.current = false; }}
                  onMouseLeave={() => { isDrawing.current = false; }}
                  onMouseMove={handleMove}
                  onTouchStart={() => { isDrawing.current = true; }}
                  onTouchEnd={() => { isDrawing.current = false; }}
                  onTouchMove={handleMove}
                />
              )}
            </div>
            {!revealed && <p className="text-[10px] text-[#555] text-center mt-1">Drag to scratch</p>}
            {revealed && (
              <button onClick={copy} className="mt-1 w-full text-center text-[11px] text-[#ff9a4d] hover:text-[#ffb347] transition-colors font-medium">
                {copied ? "Copied!" : "Tap to copy code"}
              </button>
            )}
          </div>
          {/* Gold gradient stripe */}
          <div className="h-[38px] flex items-center justify-between px-8" style={{ background: "linear-gradient(90deg, #ff9a4d, #f5c842, #ffb347, #f5c842, #ff9a4d)" }}>
            <span className="text-[9px] font-mono text-black/40 tracking-wider">{sessionId}</span>
            <span className="text-[9px] font-bold text-black/40 tracking-[2px]">EAMICO INC.</span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#e8eaed] p-5 text-center">
          <p className="text-[14px] text-[#202124] font-medium mb-1">Redeem on any Eamico product</p>
          <p className="text-[13px] text-[#5f6368] mb-4">Save this code — it&apos;s unique to you. Redeemable on any Eamico product at launch.</p>
          <div className="flex items-center justify-center gap-4">
            <a href="https://www.linkedin.com/company/eamico" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[12px] text-[#0a66c2] hover:underline font-medium">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"/></svg>
              LinkedIn
            </a>
            <a href="https://eamico.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[12px] text-[#202124] hover:underline font-medium">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
              eamico.com
            </a>
            <a href="https://reddit.com/r/eamico" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[12px] text-[#ff4500] hover:underline font-medium">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.6.11.793-.26.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/></svg>
              r/eamico
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── field renderers ── */
const TextField = ({ field, value, onChange }) => (
  <div className={field.half ? "flex-1 min-w-[200px]" : "w-full"}>
    <label className="block text-[13px] font-medium text-[#202124] mb-1.5">
      {field.label} {field.required && <span className="text-red-400">*</span>}
    </label>
    <input
      type={field.type}
      value={value || ""}
      onChange={(e) => onChange(field.name, e.target.value)}
      required={field.required}
      className="w-full px-3.5 py-2.5 border border-[#dadce0] rounded-lg text-[14px] text-[#202124] placeholder-[#80868b] focus:outline-none focus:border-[#1a73e8] focus:ring-1 focus:ring-[#1a73e8] transition-colors"
      placeholder={field.label}
      data-testid={`survey-field-${field.name}`}
    />
  </div>
);

const TextareaField = ({ field, value, onChange }) => (
  <div className="w-full">
    <label className="block text-[13px] font-medium text-[#202124] mb-1.5">
      {field.label} {field.required && <span className="text-red-400">*</span>}
    </label>
    <textarea
      value={value || ""}
      onChange={(e) => onChange(field.name, e.target.value)}
      required={field.required}
      rows={3}
      className="w-full px-3.5 py-2.5 border border-[#dadce0] rounded-lg text-[14px] text-[#202124] placeholder-[#80868b] focus:outline-none focus:border-[#1a73e8] focus:ring-1 focus:ring-[#1a73e8] transition-colors resize-none"
      placeholder="Your answer..."
      data-testid={`survey-field-${field.name}`}
    />
  </div>
);

const SelectField = ({ field, value, onChange }) => (
  <div className="w-full">
    <label className="block text-[13px] font-medium text-[#202124] mb-1.5">
      {field.label} {field.required && <span className="text-red-400">*</span>}
    </label>
    <select
      value={value || ""}
      onChange={(e) => onChange(field.name, e.target.value)}
      required={field.required}
      className="w-full px-3.5 py-2.5 border border-[#dadce0] rounded-lg text-[14px] text-[#202124] bg-white focus:outline-none focus:border-[#1a73e8] focus:ring-1 focus:ring-[#1a73e8] transition-colors"
      data-testid={`survey-field-${field.name}`}
    >
      <option value="">Select...</option>
      {field.options.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  </div>
);

const MultiSelectField = ({ field, value, onChange }) => {
  const selected = value || [];
  const toggle = (opt) => {
    const next = selected.includes(opt) ? selected.filter((v) => v !== opt) : [...selected, opt];
    onChange(field.name, next);
  };
  return (
    <div className="w-full">
      <label className="block text-[13px] font-medium text-[#202124] mb-2">
        {field.label} {field.required && <span className="text-red-400">*</span>}
      </label>
      <div className="flex flex-wrap gap-2" data-testid={`survey-field-${field.name}`}>
        {field.options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={`px-3 py-1.5 rounded-full text-[13px] border transition-all ${
              selected.includes(opt)
                ? "bg-[#e8f0fe] border-[#1a73e8] text-[#1a73e8] font-medium"
                : "bg-white border-[#dadce0] text-[#5f6368] hover:border-[#80868b]"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
};

const RatingField = ({ field, value, onChange }) => (
  <div className="w-full">
    <label className="block text-[13px] font-medium text-[#202124] mb-2">
      {field.label} {field.required && <span className="text-red-400">*</span>}
    </label>
    <div className="flex items-center gap-1" data-testid={`survey-field-${field.name}`}>
      <span className="text-[12px] text-[#80868b] mr-2">Not satisfied</span>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(field.name, n)}
          className={`w-10 h-10 rounded-full text-[14px] font-medium border transition-all ${
            value === n
              ? "bg-[#1a73e8] border-[#1a73e8] text-white"
              : "bg-white border-[#dadce0] text-[#5f6368] hover:border-[#80868b]"
          }`}
        >
          {n}
        </button>
      ))}
      <span className="text-[12px] text-[#80868b] ml-2">Very satisfied</span>
    </div>
  </div>
);

const ToggleField = ({ field, value, onChange }) => (
  <div className="w-full flex items-center justify-between py-1">
    <label className="text-[13px] font-medium text-[#202124]">{field.label}</label>
    <button
      type="button"
      onClick={() => onChange(field.name, !value)}
      className={`relative w-11 h-6 rounded-full transition-colors ${value ? "bg-[#1a73e8]" : "bg-[#dadce0]"}`}
      data-testid={`survey-field-${field.name}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${value ? "translate-x-5" : ""}`} />
    </button>
  </div>
);

const FieldRenderer = ({ field, value, onChange }) => {
  switch (field.type) {
    case "text": case "email": case "tel": return <TextField field={field} value={value} onChange={onChange} />;
    case "textarea": return <TextareaField field={field} value={value} onChange={onChange} />;
    case "select": return <SelectField field={field} value={value} onChange={onChange} />;
    case "multiselect": return <MultiSelectField field={field} value={value} onChange={onChange} />;
    case "rating": return <RatingField field={field} value={value} onChange={onChange} />;
    case "toggle": return <ToggleField field={field} value={value} onChange={onChange} />;
    default: return null;
  }
};

/* ── main component ── */
const SurveyPage = () => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [sessionId] = useState(() => crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
  const [fingerprint] = useState(() => {
    const nav = window.navigator;
    const screen = window.screen;
    const raw = [
      nav.userAgent,
      nav.language,
      nav.languages ? nav.languages.join(",") : "",
      nav.hardwareConcurrency || "",
      nav.deviceMemory || "",
      screen.width + "x" + screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      nav.maxTouchPoints || 0,
      nav.platform || "",
      !!window.indexedDB,
      !!window.sessionStorage,
      !!window.localStorage,
    ].join("|");
    let hash = 0;
    for (let i = 0; i < raw.length; i++) {
      hash = ((hash << 5) - hash + raw.charCodeAt(i)) | 0;
    }
    return "fp_" + Math.abs(hash).toString(36);
  });
  const [source] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const utm = params.get("utm_source") || params.get("ref") || params.get("source");
    if (utm) return utm;
    const ref = document.referrer || "";
    if (ref.includes("reddit.com")) return "Reddit";
    if (ref.includes("twitter.com") || ref.includes("x.com") || ref.includes("t.co")) return "Twitter / X";
    if (ref.includes("linkedin.com")) return "LinkedIn";
    if (ref.includes("news.ycombinator.com")) return "Hacker News";
    if (ref.includes("discord.com") || ref.includes("discord.gg")) return "Discord";
    if (ref.includes("facebook.com") || ref.includes("fb.com")) return "Facebook";
    if (ref.includes("instagram.com")) return "Instagram";
    if (ref.includes("google.com")) return "Google";
    if (ref) return ref.replace(/https?:\/\/(www\.)?/, "").split("/")[0];
    return "Direct";
  });

  const current = surveySteps[step];

  const savePartial = useCallback((currentStep, currentData) => {
    fetch(`${SURVEY_API}/api/survey/partial`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId, step: currentStep, data: { ...currentData, source, fingerprint } }),
    }).catch(() => {});
  }, [sessionId]);

  const handleChange = useCallback((name, value) => {
    setData((prev) => ({ ...prev, [name]: value }));
    setError("");
  }, []);

  const validate = () => {
    for (const field of current.fields) {
      if (!field.required) continue;
      const val = data[field.name];
      if (val === undefined || val === null || val === "" || (Array.isArray(val) && val.length === 0)) {
        setError(`Please fill in "${field.label}"`);
        return false;
      }
      if (field.minLength && typeof val === "string" && val.trim().length < field.minLength) {
        setError(`"${field.label}" needs at least ${field.minLength} characters — give us a real answer!`);
        return false;
      }
    }
    // Also check minLength on optional fields that have content
    for (const field of current.fields) {
      if (!field.minLength) continue;
      const val = data[field.name];
      if (val && typeof val === "string" && val.trim().length > 0 && val.trim().length < field.minLength) {
        setError(`"${field.label}" needs at least ${field.minLength} characters if you're filling it in`);
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validate()) return;
    savePartial(step, data);
    setStep((s) => Math.min(s + 1, surveySteps.length - 1));
    setError("");
  };

  const prev = () => { setStep((s) => Math.max(s - 1, 0)); setError(""); };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${SURVEY_API}/api/survey/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, sessionId, source, fingerprint }),
      });
      const resData = await res.json();
      if (resData.duplicate) {
        setError("Looks like you've already completed this survey. One response per person — but thank you!");
        setSubmitting(false);
        return;
      }
      if (!res.ok) throw new Error("Submission failed");
      setDiscountCode(resData.discountCode || "");
      setSubmitted(true);
    } catch {
      setError("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <GiftCardScreen discountCode={discountCode} sessionId={sessionId} />;
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]" data-testid="survey-page">
      <div className="max-w-2xl mx-auto px-4 py-12 sm:py-16">
        {/* Header */}
        <div className="bg-white rounded-t-2xl border border-[#e8eaed] border-b-0 p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-6">
            <img src="https://customer-assets.emergentagent.com/job_eamico-core/artifacts/uowh0b6l_Untitled%20design.png" alt="Eamico" className="h-7" />
            <span className="text-[13px] text-[#80868b] font-medium">Developer Survey</span>
          </div>
          <h1 className="text-[24px] sm:text-[28px] font-normal text-[#202124] leading-tight">
            Dev tools survey — 2 minutes, tops
          </h1>
          <p className="text-[14px] text-[#5f6368] mt-2">
            We're building new dev tools and want to understand what's actually broken. Honest answers only.
          </p>
          <div className="mt-4 mb-6 flex items-center gap-3 bg-[#e6f4ea] border border-[#ceead6] rounded-lg px-4 py-2.5">
            <span className="text-[18px]">&#127873;</span>
            <p className="text-[13px] text-[#137333] leading-snug"><strong>Finish the survey</strong> and get <strong>$100 off any Eamico product</strong> — unique discount code, just for you.</p>
          </div>

          {/* Progress */}
          <div className="flex gap-1.5">
            {surveySteps.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                  i > step ? "bg-[#e8eaed]" : ""
                }`}
                style={i <= step ? { background: "linear-gradient(90deg, #ff9a4d, #f5c842, #ffb347)" } : undefined}
              />
            ))}
          </div>
        </div>

        {/* Step card */}
        <div className="bg-white border border-[#e8eaed] p-6 sm:p-8">
          <div className="mb-1">
            <span className="text-[11px] font-bold tracking-wide uppercase text-[#1a73e8]">
              Step {step + 1} of {surveySteps.length}
            </span>
          </div>
          <h2 className="text-[18px] font-medium text-[#202124] mb-1">{current.title}</h2>
          <p className="text-[13px] text-[#80868b] mb-6">{current.description}</p>

          <div className={`space-y-5`}>
            {(() => {
              const rows = [];
              let i = 0;
              while (i < current.fields.length) {
                const f = current.fields[i];
                if (f.half && i + 1 < current.fields.length && current.fields[i + 1].half) {
                  rows.push(
                    <div key={f.name} className="flex gap-4 flex-wrap">
                      <FieldRenderer field={f} value={data[f.name]} onChange={handleChange} />
                      <FieldRenderer field={current.fields[i + 1]} value={data[current.fields[i + 1].name]} onChange={handleChange} />
                    </div>
                  );
                  i += 2;
                } else {
                  rows.push(<FieldRenderer key={f.name} field={f} value={data[f.name]} onChange={handleChange} />);
                  i++;
                }
              }
              return rows;
            })()}
          </div>

          {error && <p className="mt-4 text-[13px] text-red-500" data-testid="survey-error">{error}</p>}
        </div>

        {/* Nav buttons */}
        <div className="bg-white rounded-b-2xl border border-[#e8eaed] border-t-0 px-6 sm:px-8 py-5 flex items-center justify-between">
          <button
            onClick={prev}
            disabled={step === 0}
            className="px-5 py-2.5 text-[14px] font-medium text-[#1a73e8] hover:bg-[#f0f4ff] rounded-md transition-colors disabled:opacity-30 disabled:cursor-default"
            data-testid="survey-prev-btn"
          >
            Back
          </button>
          {step < surveySteps.length - 1 ? (
            <button
              onClick={next}
              className="px-6 py-2.5 text-[14px] font-medium text-white bg-[#1a73e8] hover:bg-[#1765cc] rounded-md transition-colors shadow-sm"
              data-testid="survey-next-btn"
            >
              {current.type === "showcase" ? "Continue" : "Next"}
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={submitting}
              className="px-6 py-2.5 text-[14px] font-medium text-white bg-[#1a73e8] hover:bg-[#1765cc] rounded-md transition-colors shadow-sm disabled:opacity-60"
              data-testid="survey-submit-btn"
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyPage;
